import fetchOrderDetailsV3 from '@dehaat/kisan-app-bl/api/fetchOrderDetailsV3';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import WrongIcon from '@/components/icons/CrossIcon';
import { ORDER_SUCCESS_DISPLAY_DURATION, PAYMENT_STATUS_POLLING_INTERVAL, PENDING_PAYMENT_DISPLAY_DURATION } from '@/constants/cart';
import { CLIENT_NAME_COOKIE, NAVIGATION_FROM_APP, NAVIGATION_FROM_APP_CODE } from '@/constants/common';
import { ORDER_STATUS } from '@/constants/order';
import SuccesGif from '@/images/payments/Done.gif';
import PaymentPendingGif from '@/images/payments/Rupee.gif';
import { getCookieValue, isFromAgcApp } from '@/utils/helper';
interface OrderStatusProps {
  orderId?: string;
  isOnlineOrder: boolean;
  redirectUrl?: string;
}
export default function OrderStatusPopup({
  orderId,
  isOnlineOrder,
  redirectUrl
}: OrderStatusProps) {
  const [status, setStatus] = useState<'pending' | 'success' | 'failure'>('pending');
  const {
    push,
    asPath,
    replace,
    locale
  } = useRouter();
  const fromApp = useMemo(() => Boolean(getCookieValue(NAVIGATION_FROM_APP)) || Boolean(getCookieValue(NAVIGATION_FROM_APP_CODE)), []);
  const fromAgc = useMemo(() => Boolean(isFromAgcApp(getCookieValue(CLIENT_NAME_COOKIE))), []);
  const url = redirectUrl ? redirectUrl : fromApp && !fromAgc ? '/orders?sh=f' : '/orders';
  const intervalId = useRef<NodeJS.Timeout | null>(null);
  const timeoutId = useRef<NodeJS.Timeout | null>(null);
  const [failureReason, setFailureReason] = useState('');
  const redirectToOrderListing = useCallback(() => {
    if (asPath.includes('/cart')) {
      replace(url);
    } else {
      push(url);
    }
  }, [asPath, push, replace, url]);
  const clearTimer = useCallback(() => {
    if (intervalId.current) clearInterval(intervalId.current);
    if (timeoutId.current) clearTimeout(timeoutId.current);
  }, []);
  useEffect(() => {
    const fetchOrderStatus = async () => {
      try {
        const orderDetails = await fetchOrderDetailsV3(orderId as string, locale);
        const ispaymentSuccess = orderDetails.online_payment_order && orderDetails?.online_payment.payment_status && orderDetails?.online_payment.payment_status !== ORDER_STATUS.Pending;
        if (ispaymentSuccess && orderDetails?.online_payment.payment_status === ORDER_STATUS.Success) {
          setStatus('success');
          if (intervalId.current) clearInterval(intervalId.current);
          if (timeoutId.current) clearTimeout(timeoutId.current);
          setTimeout(() => {
            if (intervalId.current || timeoutId.current) redirectToOrderListing();
            intervalId.current = null;
            timeoutId.current = null;
          }, ORDER_SUCCESS_DISPLAY_DURATION);
        } else if (ispaymentSuccess && orderDetails?.online_payment.payment_status === ORDER_STATUS.Failed) {
          setStatus('failure');
          setFailureReason(orderDetails?.online_payment?.reason || '');
          if (intervalId.current) clearInterval(intervalId.current);
          if (timeoutId.current) clearTimeout(timeoutId.current);
        }
      } catch (err) {
        console.error('Failed to fetch order status:', err);
        setStatus('pending');
        clearTimer();
      }
    };
    if (isOnlineOrder) {
      if (orderId) {
        intervalId.current = setInterval(fetchOrderStatus, PAYMENT_STATUS_POLLING_INTERVAL);
        timeoutId.current = setTimeout(() => {
          if (intervalId.current) {
            clearInterval(intervalId.current);
            redirectToOrderListing();
          }
        }, PENDING_PAYMENT_DISPLAY_DURATION);
      }
    } else {
      timeoutId.current = setTimeout(() => {
        redirectToOrderListing();
      }, ORDER_SUCCESS_DISPLAY_DURATION);
    }
    return () => {
      clearTimer();
    };
  }, [clearTimer, isOnlineOrder, locale, orderId, redirectToOrderListing, status]);
  if (status === 'success' || !isOnlineOrder) {
    return <div className="z-50 fixed inset-0 flex flex-col justify-center items-center bg-primary-100 bg-white p-4">
        <Image src={SuccesGif} alt="payment-sccess" className="max-w-sm" />

        <div className="mt-4 font-semibold text-white text-lg">
          Order Placed Successfully
        </div>
      </div>;
  }
  if (status === 'failure') {
    return <div className="z-50 fixed inset-0 flex justify-center items-center">
        <div className="flex flex-col items-center gap-4 bg-white shadow-[0_-2px_6px_0_rgba(0,0,0,0.16)] p-4 border border-neutral-20 rounded-xl w-[90%] max-w-sm">
          <div className="flex flex-col justify-center items-center bg-error-10 py-9 rounded-t-lg w-full">
            <WrongIcon width={64} height={64} />

            <div className="mt-6 font-bold text-error-100 text-2xl">
              Order Failed
            </div>
          </div>

          <div className="font-normal text-neutral-90 text-base text-center">
            {failureReason}
          </div>

          <hr className="shadow-[0_-2px_6px_0_rgba(0,0,0,0.16)] mx-[-1rem] border border-neutral-20 w-[calc(100%+2rem)]" />

          <button className="bg-white py-4 border border-neutral-20 rounded-lg w-full font-semibold text-primary-100 text-base text-center" onClick={() => redirectToOrderListing()}>
            Okay
          </button>
        </div>
      </div>;
  }
  return <div className="z-50 fixed inset-0 flex flex-col justify-center items-center bg-[#FFF3C2] bg-white p-4" data-sentry-component="OrderStatusPopup" data-sentry-source-file="OrderStatusPopup.tsx">
      <Image src={PaymentPendingGif} alt="payment-pending" className="max-w-sm" data-sentry-element="Image" data-sentry-source-file="OrderStatusPopup.tsx" />
      <div className="mt-4 font-semibold text-[#8C6500] text-lg">
        Waiting for Payment
      </div>
    </div>;
}